import {Component, DoCheck, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {DatePipe} from '@angular/common';
import {NavigationEnd, Router} from '@angular/router';

import {GoogleAnalyticsService, UtilService} from '../../../core';
import {AirComponentType, SensorUnitPipe} from '../../../shared';
import {DashboardService} from '../../shared';
import {D3Service, ITimeLine, ITimePoint, ITooltipContent, MultiGraphService, TimelineService} from '../shared';
// Child components
import {XAxisComponent} from './x-axis/x-axis.component';
import {YAxisComponent} from './y-axis/y-axis.component';
import {DottedLineComponent} from './dotted-line/dotted-line.component';
import {LineListComponent} from './line-list/line-list.component';
import {BigDotComponent} from './big-dot/big-dot.component';
import {SmallDotComponent} from './small-dot/small-dot.component';

import {IndicatorComponent} from './indicator/indicator.component';

import {ILineTooltipInput, LineTooltipComponent} from './line-tooltip/line-tooltip.component';
import {IGlobalTooltipInput} from './global-tooltip/global-tooltip.component';
import {INotiTooltipInput, NotiTooltipComponent} from './noti-tooltip/noti-tooltip.component';

import {InfoPopupComponent} from './info-popup/info-popup.component';

import {Subscription, Observable} from 'rxjs';
import * as d3 from 'd3';
import {Line} from 'd3';
import {ScaleThreshold} from 'd3-scale';
import {SelfOrganizationUser} from '../../../afb-api/models/organization-user';

import * as fromAfbApi from '../../../afb-api/reducers';
import {select, Store} from '@ngrx/store';
import {filter} from 'rxjs/operators';
import {Device} from '../../../afb-api/models/device';
import {Airnote} from '../../../afb-api/models/airnote';
import {TriggerEvent} from '../../../afb-api/models/trigger-event';

@Component({
  selector: 'awair-timeline-graph',
  templateUrl: './timeline-graph.component.html',
  styleUrls: ['./timeline-graph.component.scss']
})

export class TimelineGraphComponent implements OnChanges, OnInit, DoCheck, OnDestroy {

  @ViewChild('timelineGraphContainer') timelineGraphContainer: ElementRef;
  @ViewChild('graphContainer') graphContainer: ElementRef;
  @ViewChild('workArea') workArea: any;
  @ViewChild('svgGroup') svgGroup: ElementRef;
  @ViewChild('dottedLine') dottedLine: DottedLineComponent;
  @ViewChild('lineList') lineList: LineListComponent;
  @ViewChild('bigDot') bigDot: BigDotComponent;
  @ViewChild('smallDot') smallDot: SmallDotComponent;
  @ViewChild('xAxis') xAxis: XAxisComponent;
  @ViewChild('yAxis') yAxis: YAxisComponent;

  @ViewChild('indicator') indicator: IndicatorComponent;

  @ViewChild('lineTooltip') lineTooltip: LineTooltipComponent;
  @ViewChild('notiTooltip') notiTooltip: NotiTooltipComponent;

  @ViewChild('infoPopup') infoPopup: InfoPopupComponent;

  @Input() fromDate: Date;
  @Input() toDate: Date;
  @Input() timelineList: ITimeLine[] = [];
  @Input() dottedTimeline: ITimeLine;
  @Input() selectedDeviceList: Device[];
  @Input() airNoteListOfDeviceList: Array<Airnote[]>;
  @Input() triggerEventListOfDeviceList: Array<TriggerEvent[]>;

  @Input() type: AirComponentType;
  @Input() unit: string;

  source: AirComponentType;
  xDate: Date;
  lineColorList: string[] = ['#66A7FF', '#AA76F1', '#60DAEF', '#EC8AD0', '#7FB6FF', '#BC8BFF', '#4CE5FF', '#FF9DE3'];
  // Indicator inputs
  isPinned: boolean = false;
  isIndicatorDotHovered: boolean = false;
  indicatorDotPosList: Array<{ x: number, y: number }>;
  // Tooltip
  pinnedDeviceIndex: number = -1;
  pinnedSource: string = '';

  // Tooltip Inputs
  globalTooltipInput: IGlobalTooltipInput;
  isGlobalTooltipVisible: boolean = false;

  lineTooltipInput: ILineTooltipInput;
  pinInput: { pinSource: AirComponentType, isPinned?: boolean } = {
    isPinned: false,
    pinSource: null
  };
  selectedAirNote: Airnote;

  notiTooltipInput: INotiTooltipInput;
  isNotiTooltipPinned: boolean = false;

  // Tooltip for event trigger
  pinnedTriggerEvent: TriggerEvent;

  // SVG
  width: number;
  height: number;
  // Scales
  xScale: any;
  yScale: any;
  yMax: number;
  yMin: number;
  colorThresholdScale: ScaleThreshold<number, number>;
  // Line
  lineGen: Line<any>;
  // URL
  sanitizedUrl: SafeResourceUrl;
  // Info popup
  isInfoPopup: boolean = false;
  enableBackdrop: boolean;

  selfOrganizationUser$: Observable<SelfOrganizationUser> = this.afbApiStore.pipe(
    select(fromAfbApi.selfOrganizationUser),
    filter((selfOrganizationUser: SelfOrganizationUser) => selfOrganizationUser != null)
  );

  // For DoCheck
  private oldTimelineList: ITimeLine[];
  private oldDottedTimeline: ITimeLine;
  private oldAirNoteListOfDeviceList: Array<Airnote[]>;
  private oldTriggerEventListOfDeviceList: Array<TriggerEvent[]>;
  // Margin is required to show x and y axis tick texts
  private margin = {
    top: 6,
    right: 18,
    bottom: 30,
    left: 40
  };
  private routeSubs: Subscription;

  constructor(private afbApiStore: Store<fromAfbApi.State>,
              private timelineService: TimelineService,
              private multiGraphService: MultiGraphService,
              private d3Service: D3Service,
              private utilService: UtilService,
              private dashboardService: DashboardService,
              private googleAnalyticsService: GoogleAnalyticsService,
              private datePipe: DatePipe,
              private sensorUnit: SensorUnitPipe,
              private router: Router,
              private sanitizer: DomSanitizer
  ) {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.enableBackdrop = this.router.url === '/dashboard' ? true : false;
      }
    });

    /**
     * Multigraph global broadcasts
     */
    this.multiGraphService.hoverAnnounced$.subscribe((xDate: Date) => {
      // Latest hovered timestamp
      this.xDate = xDate;
      this.showHoverLine(xDate);
      // Global tooltip
      this.globalTooltipInput = this.getGlobalTooltipInput(xDate, this.selectedDeviceList,
        this.timelineList, this.type, this.xScale, this.unit);
      this.isGlobalTooltipVisible = true;
    });
    this.multiGraphService.clickAnnounced$.subscribe((clickObj: { isPinned: boolean, source: AirComponentType }) => {
      this.isPinned = clickObj.isPinned;
      this.source = clickObj.source;

      // Broadcast to timeline if any of pinned noti tooltip has been unpinned (unpin action on subscription) (should be always one)
      if ((this.pinInput.isPinned && this.selectedAirNote) || this.notiTooltip.isPinned) {
        this.multiGraphService.announceUnpinnedNoti();
      }

      // Unpin sensor noti tooltip if it's pinned
      if (this.notiTooltip.isPinned) {
        this.pinnedTriggerEvent = null;
        // this.notiTooltip.unpinTooltip();
        this.isNotiTooltipPinned = false;
        this.notiTooltip.removeTooltip();
      }
      // Unpin airnote tooltip if it's pinned
      if (this.pinInput.isPinned) {
        this.pinInput = {isPinned: false, pinSource: this.source};
        this.lineTooltip.removeTooltip();
        this.pinnedDeviceIndex = -1;
        if (this.selectedAirNote) {
          this.selectedAirNote = null;
        }
      }
      // Global pin check
      this.isPinned ? this.indicator.pinIndicator() : this.indicator.unpinIndicator();
    });
    this.multiGraphService.outAnnounced$.subscribe(() => {
      if (!this.isPinned) {
        this.indicator.removeIndicator();
        this.lineTooltip.removeTooltip();

        // Global Tooltip
        this.isGlobalTooltipVisible = false;
      }
    });

    /**
     * Multigraph indicator broadcasts
     */
    this.multiGraphService.indicatorDotHoverAnnounced$.subscribe((hoverObj: { hoveredIndex: number, deviceIndex: number }) => {
      if (this.pinnedDeviceIndex === -1) {
        this.lineTooltipInput = this.getLineTooltipInput(hoverObj);
        this.isGlobalTooltipVisible = false;
      }
    });
    this.multiGraphService.pinTooltipAnnounced$.subscribe(
      (clickObj: { hoveredIndex: number, sourceType: AirComponentType, deviceIndex: number, isInvokedFromNoti: boolean }) => {
        // If notiTooltip is pinned previously, remove it
        if (this.notiTooltip.isPinned) {
          this.pinnedTriggerEvent = null;
          this.isNotiTooltipPinned = false;
          this.notiTooltip.removeTooltip();
        }

        // Check if clicked tooltip contains airnote
        // Before checking, check if such timeline exists since component like dust has no same length with other components
        if (this.timelineList[clickObj.deviceIndex]) {
          this.selectedAirNote = this.bigDot.hasAirNote(
            new Date(this.timelineList[clickObj.deviceIndex][clickObj.hoveredIndex].x), clickObj.deviceIndex);
        }

        if (this.pinnedDeviceIndex === -1) {
          // Nothing is pinned yet,
          this.isPinned = true;
          this.pinnedDeviceIndex = clickObj.deviceIndex;
          this.pinnedSource = clickObj.sourceType;
          this.indicator.pinIndicator();

          this.pinInput = {isPinned: true, pinSource: clickObj.sourceType};

          this.isGlobalTooltipVisible = false;

        } else {
          // If any of other dots is clicked,
          this.pinInput = {isPinned: false, pinSource: clickObj.sourceType};

          this.pinnedDeviceIndex = clickObj.deviceIndex;
          this.pinnedSource = clickObj.sourceType;
          this.source = clickObj.sourceType;
          this.indicator.pinIndicator();
          this.lineTooltipInput = this.getLineTooltipInput(clickObj);
          this.pinInput = {isPinned: true, pinSource: clickObj.sourceType};

          this.isGlobalTooltipVisible = false;
        }

        if (clickObj.sourceType === this.type && this.selectedAirNote) {
          // Broadcast if any airnote msg is clicked
          this.multiGraphService.announcePinnedNoti(this.selectedAirNote);
        } else if (clickObj.sourceType === this.type && !this.selectedAirNote) {
          // If click tooltip enabled without any selected air note, announce unpinned noti
          // Since this is being called too frequent, add a variable in the service that shares if noti is currently on or off
          if (!clickObj.isInvokedFromNoti) {
            // This is to prevent force closing of detailed feed pane when pinning is invoked from feed pane
            this.multiGraphService.announceUnpinnedNoti();
          }
        }
      });
    this.multiGraphService.unpinTooltipAnnounced$.subscribe(() => {
      this.isPinned = false;
      this.pinnedDeviceIndex = -1;
      this.pinnedSource = null;

      this.pinInput = {isPinned: false, pinSource: this.source};
    });
    this.multiGraphService.indicatorDotOutAnnounced$.subscribe(() => {
      if (this.pinnedDeviceIndex === -1) {
        this.lineTooltip.removeTooltip();
        this.isGlobalTooltipVisible = true;
      }
    });
    /**
     * Multigraph trigger event broadcasts
     */
    this.multiGraphService.pinTeAnnounced$.subscribe(
      (pinnedObj: { triggerEvent: TriggerEvent, sourceType: string, deviceIndex: number }) => {
        // Unpin airnote tooltip if it's pinned
        if (this.pinInput.isPinned) {
          this.pinInput = {isPinned: false, pinSource: this.source};
          this.lineTooltip.removeTooltip();
          this.pinnedDeviceIndex = -1;
        }

        if (!this.pinnedTriggerEvent && this.type === pinnedObj.sourceType) {
          // If tooltip wasn't selected previously in this graph but selected this time
          this.onHoverTriggerEvent({'triggerEvent': pinnedObj.triggerEvent, 'deviceIndex': pinnedObj.deviceIndex});
          this.pinnedTriggerEvent = pinnedObj.triggerEvent;
          // this.notiTooltip.pinTooltip();
          setTimeout(() => {
            this.isNotiTooltipPinned = true;
          }, 1);

          this.multiGraphService.announcePinnedNoti(this.pinnedTriggerEvent);

        } else if (this.pinnedTriggerEvent && this.type !== pinnedObj.sourceType) {
          // Unpin tooltip if tooltip was previously pinned in this graph but not this time
          this.pinnedTriggerEvent = null;
          // this.notiTooltip.unpinTooltip();
          this.isNotiTooltipPinned = false;
        } else if (this.pinnedTriggerEvent && this.type === pinnedObj.sourceType) {
          if (this.pinnedTriggerEvent.deviceType === pinnedObj.triggerEvent.deviceType &&
            this.pinnedTriggerEvent.deviceId === pinnedObj.triggerEvent.deviceId &&
            this.pinnedTriggerEvent.component === pinnedObj.triggerEvent.component &&
            this.pinnedTriggerEvent.timestamp === pinnedObj.triggerEvent.timestamp) {

            // If tooltip was previously pinned in this graph and same trigger event is selected again
            this.pinnedTriggerEvent = null;
            // this.notiTooltip.unpinTooltip();
            this.isNotiTooltipPinned = false;
            // this.notiTooltip.removeTooltip();
            this.multiGraphService.announceUnpinnedNoti();

          } else {
            // If tooltip was previously pinned in this graph but new trigger event is selected
            // this.notiTooltip.unpinTooltip();
            this.isNotiTooltipPinned = false;
            this.onHoverTriggerEvent({'triggerEvent': pinnedObj.triggerEvent, 'deviceIndex': pinnedObj.deviceIndex});
            this.pinnedTriggerEvent = pinnedObj.triggerEvent;
            // this.notiTooltip.pinTooltip();
            setTimeout(() => {
              this.isNotiTooltipPinned = true;
            }, 1);
            this.multiGraphService.announcePinnedNoti(this.pinnedTriggerEvent);
          }
        }
      });
    this.multiGraphService.removeTeAnnounced$.subscribe(() => {
      if (this.pinnedTriggerEvent) {
        this.onClickTriggerEvent(this.pinnedTriggerEvent);
        this.onOutTriggerEvent();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    for (const propname in changes) {
      if (propname === 'type' && this.type != null) {
        // Need to convert to trusted url since browser won't allow user to access such unsafe url
        this.sanitizedUrl = this.sanitizer.bypassSecurityTrustResourceUrl('assets/images/components/ic_' + this.type + '.svg');
        this.colorThresholdScale = this.d3Service.getOmniMintColorThreshold(this.type);
      }
    }
  }

  ngOnInit(): void {
    this.init();
  }

  ngDoCheck(): void {
    // Update timelines
    if (this.timelineList !== this.oldTimelineList) {
      this.oldTimelineList = this.timelineList;

      if (this.fromDate && this.toDate) {
        const allTimelineList: ITimeLine[] = this.mergeTimelineToList(this.timelineList, this.dottedTimeline);
        this.updateLine(allTimelineList);
      }
    }

    if (this.dottedTimeline !== this.oldDottedTimeline) {
      this.oldDottedTimeline = this.dottedTimeline;

      if (this.fromDate && this.toDate) {
        const allTimelineList: ITimeLine[] = this.mergeTimelineToList(this.timelineList, this.dottedTimeline);
        this.updateLine(allTimelineList);
      }
    }

    // Update notifications
    if (this.airNoteListOfDeviceList !== this.oldAirNoteListOfDeviceList) {
      this.oldAirNoteListOfDeviceList = this.airNoteListOfDeviceList;
      if (this.xAxis && this.yAxis) {
        this.updateNotification(this.svgGroup, this.xScale, this.yScale, this.yMin,
          this.airNoteListOfDeviceList, this.triggerEventListOfDeviceList);
      }
    }

    if (this.triggerEventListOfDeviceList !== this.oldTriggerEventListOfDeviceList) {
      this.oldTriggerEventListOfDeviceList = this.triggerEventListOfDeviceList;

      if (this.xAxis && this.yAxis) {
        this.updateNotification(this.svgGroup, this.xScale, this.yScale, this.yMin,
          this.airNoteListOfDeviceList, this.triggerEventListOfDeviceList);
      }
    }
  }

  ngOnDestroy() {
    if (this.routeSubs) {
      this.routeSubs.unsubscribe();
    }
  }

  /**
   * Events of global area
   */
  onHoverTimeline(relativePos: { x: number, y: number }): void {
    if (!this.isPinned) {
      // Since there're dates on all x, there's no need to double check if timeline exists (parsed ahead)
      if (this.timelineList && this.dottedTimeline) {
        const absXDate = this.xScale.invert(relativePos.x);
        const bisector = d3.bisector((d: any) => new Date(d.x)).left;

        // Should filter valid timeline since such cases(dust) have timelineList
        // with length more than 1 but has data only at index 2 or above
        let validTimeline;
        for (let i = 0; i < this.timelineList.length; i++) {
          if (this.timelineList[i] != null) {
            validTimeline = this.timelineList[i];
            break;
          }
        }

        if (validTimeline) {
          const index: number = bisector(validTimeline, absXDate, 1) - 1;

          const d0 = validTimeline[index - 1];
          const d1 = validTimeline[index];

          let finalIndex: number;
          // Get the index of nearest mouseover date
          if (d0) {
            finalIndex = (absXDate.getTime() - new Date(d0.x).getTime()) > (new Date(d1.x).getTime() - absXDate.getTime()) ?
              index : index - 1;
          } else {
            finalIndex = index;
          }
          // Broadcast hover
          this.multiGraphService.announceHover(new Date(validTimeline[finalIndex].x));

        } else {
          // Other timelines should be hovered through dotted line even if timeline that is being hovered is empty
          const index: number = bisector(this.dottedTimeline, absXDate, 1) - 1;
          // Broadcast hover
          this.multiGraphService.announceHover(new Date(this.dottedTimeline[index].x));
        }
      }
    }
  }

  onClickTimeline(): void {
    let source: AirComponentType;

    if (this.isPinned) {
      this.isPinned = false;
      source = null;

    } else {
      this.isPinned = true;
      source = this.type;
    }

    // Broadcast click
    this.multiGraphService.announceClick(this.isPinned, source);
  }

  onOutTimeline(): void {
    // Should put setTimeout since this is called before onHoverIndicatorDot which sets this.isIndicatorDotHovered to true
    setTimeout(() => {
      if (!this.isPinned && !this.isIndicatorDotHovered) {
        // Broadcast out
        this.multiGraphService.announceOut();
      }
    }, 1);
  }

  /**
   * Events of indicator dots
   */
  onHoverIndicatorDot(hoveredObj: { relativePos: { x: number, y: number }, deviceIndex: number, value: number }): void {
    this.isIndicatorDotHovered = true;

    const mouseDate: Date = this.xScale.invert(hoveredObj.relativePos.x);
    const bisector = d3.bisector((datum: any) => new Date(datum.x)).left;

    // Get left, right data of hovered index
    const index: number = bisector(this.timelineList[hoveredObj.deviceIndex], mouseDate, 1);
    const d0 = this.timelineList[hoveredObj.deviceIndex][index - 1];
    const d1 = this.timelineList[hoveredObj.deviceIndex][index];

    const hoveredIndex: number = d1.y === hoveredObj.value ? index : index - 1;

    // Broadcast indicator hover
    this.multiGraphService.announceIndicatorDotHover(hoveredIndex, hoveredObj.deviceIndex);
  }

  onClickIndicatorDot(clickedObj: { relativePos: { x: number, y: number }, deviceIndex: number, value: number }): void {
    if (this.pinnedSource === this.type && this.pinnedDeviceIndex === clickedObj.deviceIndex) {
      // Remove if same indicator dot is clicked
      this.multiGraphService.announceUnpinTooltip();

      // Broadcast that tooltip is unpinned if the selected one contained noti
      if (this.selectedAirNote) {
        this.multiGraphService.announceUnpinnedNoti();
      }

    } else {
      const mouseDate: Date = this.xScale.invert(clickedObj.relativePos.x);
      const bisector = d3.bisector((datum: any) => new Date(datum.x)).left;

      // Get left, right data of hovered index
      const index: number = bisector(this.timelineList[clickedObj.deviceIndex], mouseDate, 1);
      const d0 = this.timelineList[clickedObj.deviceIndex][index - 1];
      const d1 = this.timelineList[clickedObj.deviceIndex][index];

      const hoveredIndex: number = d1.y === clickedObj.value ? index : index - 1;

      this.multiGraphService.announcePinTooltip(hoveredIndex, this.type, clickedObj.deviceIndex, false);
    }
  }

  onOutIndicatorDot(): void {
    this.isIndicatorDotHovered = false;

    // Broadcast indicator out
    this.multiGraphService.announceIndicatorDotOut();
  }

  /**
   * Events of trigger event dots
   */
  onHoverTriggerEvent(hoveredObj: { triggerEvent: TriggerEvent, deviceIndex: number }): void {
    const relPos = {
      x: this.xScale(new Date(hoveredObj.triggerEvent.timestamp)),
      y: this.yScale(this.yMin)
    };
    // Get absolute position relative to container
    const workAreaClientRect = this.workArea.el.nativeElement.getBoundingClientRect();
    const graphContainerRect = this.graphContainer.nativeElement.getBoundingClientRect();

    const tooltipPos = {
      x: (workAreaClientRect.left + relPos.x) - graphContainerRect.left,
      y: (workAreaClientRect.top + relPos.y) - graphContainerRect.top
    };

    this.notiTooltipInput = {
      date: new Date(hoveredObj.triggerEvent.timestamp),
      position: tooltipPos,
      selectedDevice: this.selectedDeviceList[hoveredObj.deviceIndex],
      type: this.type,
      value: hoveredObj.triggerEvent.content.rawValue
    };
  }

  onClickTriggerEvent(triggerEvent: TriggerEvent): void {
    const deviceIndex: number = this.selectedDeviceList.findIndex((device: Device) => {
      return device.deviceId === triggerEvent.deviceId && device.deviceType === triggerEvent.deviceType;
    });

    this.multiGraphService.announcePinTe(triggerEvent, this.type, deviceIndex);
  }

  onOutTriggerEvent(): void {
    if (!this.isNotiTooltipPinned) {
      this.notiTooltip.removeTooltip();
    }
  }

  onInfoPopupEvent(event: MouseEvent, infoPopupHeader: HTMLElement): void {
    if (!(infoPopupHeader.contains(event.target as HTMLElement))) {
      if (this.infoPopup != null && this.infoPopup.el.nativeElement.contains(event.target as HTMLElement)) {
        if ((event.target as HTMLElement).id === 'close-button') {
          // If selected element is close button, then close
          this.isInfoPopup = false;

          if (this.enableBackdrop) {
            this.timelineGraphContainer.nativeElement.style.zIndex = 10;
            this.dashboardService.backdropDeviceGrid(false);
            this.dashboardService.backdropTimeline(false);
          }
        } else {
          // Don't close if selected element is the popup
          this.isInfoPopup = true;

          if (this.enableBackdrop) {
            this.timelineGraphContainer.nativeElement.style.zIndex = 650;
            setTimeout(() => {
              this.dashboardService.backdropDeviceGrid(true, '#212528', 0.1, 600);
              this.dashboardService.backdropTimeline(true, '#212528', 0.1, 600);
            }, 1);
          }
        }
      } else {
        if (this.isInfoPopup) {
          // If clicked outside, then close
          this.isInfoPopup = false;

          if (this.enableBackdrop) {
            this.timelineGraphContainer.nativeElement.style.zIndex = 10;
            this.dashboardService.backdropDeviceGrid(false);
            this.dashboardService.backdropTimeline(false);
          }
        }
      }

    } else {
      // If selected element is not info-popup header, make it toggle
      if (this.isInfoPopup) {
        this.isInfoPopup = false;
        if (this.enableBackdrop) {
          this.timelineGraphContainer.nativeElement.style.zIndex = 10;
          this.dashboardService.backdropDeviceGrid(false);
          this.dashboardService.backdropTimeline(false);
        }

      } else {
        this.googleAnalyticsService.emitEvent('Info Popup', 'click', (this.type.charAt(0).toUpperCase() + this.type.slice(1)));
        this.isInfoPopup = true;
        if (this.enableBackdrop) {
          this.timelineGraphContainer.nativeElement.style.zIndex = 650;
          setTimeout(() => {
            this.dashboardService.backdropDeviceGrid(true, '#212528', 0.1, 600);
            this.dashboardService.backdropTimeline(true, '#212528', 0.1, 600);
          }, 1);
        }
      }
    }
  }

  private sortByDateAscending(a: TriggerEvent, b: TriggerEvent): number {
    // Dates will be cast to numbers automagically:
    return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
  }

  private showHoverLine(xDate: Date): void {
    if (this.timelineList && this.timelineList.length > 0) {
      // Should filter valid timeline since such cases(dust) have timelineList
      // with length more than 1 but has data only at index 2 or above
      const validTimelineList = this.timelineList.filter((timeline: ITimeLine) => timeline != null);

      if (validTimelineList.length > 0) {
        const bisector = d3.bisector((d: any) => new Date(d.x)).left;
        // Since timelines have been parsed in ahead to have same length and indexes so just get index of the first timeline
        const index: number = bisector(validTimelineList[0], xDate, 1);

        const d0 = validTimelineList[0][index - 1];
        const d1 = validTimelineList[0][index];

        let finalIndex: number;
        // Get the index of nearest mouseover date
        if (d0 != null) {
          finalIndex = (xDate.getTime() - new Date(d0.x).getTime()) > (new Date(d1.x).getTime() - xDate.getTime()) ?
            index : index - 1;
        } else {
          finalIndex = index;
        }

        const hoveredDataList: ITimePoint[] = [];
        for (let i = 0; i < this.timelineList.length; i++) {
          // Glows have undefined timeline at dust,
          if (this.timelineList[i] != null) {
            hoveredDataList.push(this.timelineList[i][finalIndex]);
          } else {
            hoveredDataList.push(null);
          }
        }

        // Should filter out ahead to prevent error when first index is undefined / null
        const filteredHoveredDataList = hoveredDataList.filter((timePoint: ITimePoint) => timePoint != null);
        this.indicatorDotPosList = [];

        if (filteredHoveredDataList.map((hoveredData: ITimePoint) => hoveredData.y).filter((y: number) => y != null).length > 0) {
          // Show tooltip if all hovered time points' y value are not null

          // Populate indicatorDotPosList
          for (let i = 0; i < hoveredDataList.length; i++) {
            if (hoveredDataList[i] != null) {
              this.indicatorDotPosList.push({
                x: this.xScale(new Date(hoveredDataList[i].x)),
                y: hoveredDataList[i].y != null ? this.yScale(hoveredDataList[i].y) : null
              });
            } else {
              this.indicatorDotPosList.push(null);
            }
          }

          // Hovered data list should have null value if itself is null or y value of it is null
          this.indicator.positionIndicatorList(this.indicatorDotPosList, this.colorThresholdScale, this.type, this.height,
            hoveredDataList.map((hoveredData: ITimePoint) => hoveredData ? (hoveredData.y ? hoveredData.y : null) : null));
        } else {
          // All hovered time points is null
          this.indicator.removeIndicator();
        }
      }
    }
  }

  private getLineTooltipInput(hoverObj: { hoveredIndex: number, deviceIndex: number }): ILineTooltipInput {
    let lineTooltipInput: ILineTooltipInput;

    // To prevent error when such component timeline doesn't exist on the hovered device unlike the hovered one
    if (this.timelineList[hoverObj.deviceIndex] != null) {
      const finalTimePoint: ITimePoint = this.timelineList[hoverObj.deviceIndex][hoverObj.hoveredIndex];

      // Get relative position given finalTimePoint
      const relPos = {
        x: this.xScale(new Date(finalTimePoint.x)),
        y: this.yScale(finalTimePoint.y)
      };

      // Get absolute position relative to container
      const workAreaClientRect = this.workArea.el.nativeElement.getBoundingClientRect();
      const graphContainerRect = this.graphContainer.nativeElement.getBoundingClientRect();

      const tooltipPos = {
        x: (workAreaClientRect.left + relPos.x) - graphContainerRect.left,
        y: (workAreaClientRect.top + relPos.y) - graphContainerRect.top
      };

      lineTooltipInput = {
        type: this.type,
        date: new Date(finalTimePoint.x),
        position: tooltipPos,
        selectedDevice: this.selectedDeviceList[hoverObj.deviceIndex],
        value: finalTimePoint.y
      };
    }

    return lineTooltipInput;
  }

  private getGlobalTooltipInput(
    xDate: Date, selectedDeviceList: Device[],
    timelineList: ITimeLine[], airComponent: AirComponentType, xScale: any, unit: string): IGlobalTooltipInput {

    const strDate: string = xDate.toISOString();
    const contentList: ITooltipContent[] = [];

    // No need to convert celsius to fahrenheit since they are converted ahead in timeline
    for (let i = 0; i < selectedDeviceList.length; i++) {
      if (timelineList[i] != null) {
        contentList.push({
          'device_name': selectedDeviceList[i].deviceName,
          'value': timelineList[i].find((timePoint: ITimePoint) => timePoint.x === strDate).y
        });
      }
    }

    const relPosX = xScale(xDate);
    const workAreaClientRect = this.workArea.el.nativeElement.getBoundingClientRect();
    const graphContainerRect = this.graphContainer.nativeElement.getBoundingClientRect();

    return {
      date: xDate,
      unit: unit,
      contents: contentList,
      position: {
        x: (workAreaClientRect.left + relPosX) - graphContainerRect.left,
        y: (workAreaClientRect.top - graphContainerRect.top)
      }
    };
  }

  private init(): void {
    // Get container sizes
    const containerWidth = this.d3Service.getPureWidth(this.graphContainer.nativeElement);
    const containerHeight = this.d3Service.getPureHeight(this.graphContainer.nativeElement);

    // The actual work area where paddings & margins aren't included
    this.width = containerWidth - this.margin.left - this.margin.right;
    this.height = containerHeight - this.margin.top - this.margin.bottom;

    this.d3Service.getFlexSVG(this.graphContainer.nativeElement, containerWidth, containerHeight, this.margin);
  }

  private updateLine(allTimelineList: ITimeLine[]): void {
    if (allTimelineList != null) {
      // Init x scale
      this.xScale = this.d3Service.getTimeScaleX(this.width, undefined, this.fromDate, this.toDate);

      // Init y scale
      this.yMax = this.timelineService.getTimelineMaxProp(allTimelineList, 'y');
      this.yMin = this.timelineService.getTimelineMinProp(allTimelineList, 'y');

      this.yScale = this.d3Service.getScaleY(this.height, undefined, this.yMin, this.yMax);

      // Init line generator
      this.lineGen = this.d3Service.getTimelineGenerator(this.xScale, this.yScale);

      this.xAxis.update(this.width, this.height, this.toDate, this.xScale);
      this.yAxis.update(this.width, this.height, this.yScale, this.yMin, this.yMax);
      this.dottedLine.update(this.dottedTimeline, this.lineGen);
      this.lineList.update(this.timelineList, this.lineGen, this.lineColorList);

      this.updateNotification(this.svgGroup, this.xScale, this.yScale, this.yMin,
        this.airNoteListOfDeviceList, this.triggerEventListOfDeviceList);
    }
  }

  private updateNotification(svgGroup: any, xScale: any, yScale: any, yMin: number,
                             airNoteListOfDeviceList: Array<Airnote[]>, triggerEventListOfDeviceList: Array<TriggerEvent[]>): void {

    this.bigDot.update(airNoteListOfDeviceList, xScale, yScale, this.type, this.lineColorList);
    this.smallDot.update(svgGroup, triggerEventListOfDeviceList, xScale, yScale, yMin);

    // Update pinned air note if it was created or edited
    if (this.pinnedDeviceIndex !== -1 && this.xDate) {
      // Value at xDate is present if it's the one that has been pinned/hovered
      this.selectedAirNote = this.bigDot.hasAirNote(this.xDate, this.pinnedDeviceIndex);
    }
  }

  private mergeTimelineToList(timelineList: ITimeLine[], targetTimeline: ITimeLine): ITimeLine[] {
    const allTimelineList: ITimeLine[] = [];

    if (this.dottedTimeline) {
      allTimelineList.push(this.dottedTimeline);
    }
    if (this.timelineList) {
      for (let i = 0; i < this.timelineList.length; i++) {
        if (this.timelineList[i] != null) {
          allTimelineList.push(this.timelineList[i]);
        }
      }
    }

    return allTimelineList;
  }
}
